import { maunaSpaceAPI } from '@/api';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertCircle,
  Eye,
  MousePointerClick,
  RefreshCw,
  ScanSearch,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

type PageRoute =
  | '/'
  | '/map'
  | '/info'
  | '/mission'
  | '/mycoupon'
  | '/golf'
  | '/golf/xr'
  | '/space/xr'
  | '/login'
  | '/user'
  | '/mission/rank';

type PageMetricItem = {
  key: string;
  label: string;
  total: number;
};

type PageMetric = {
  eventName: string;
  property: string | null;
  description: string;
  total: number;
  items: PageMetricItem[];
  status: 'success' | 'error';
};

type PageAnalyticsResponse = {
  success: boolean;
  period: { startDate: string; endDate: string };
  page: { route: PageRoute; title: string; description: string };
  views: { total: number; unique: number };
  interactionTotal: number;
  metrics: PageMetric[];
};

type Props = {
  startDate: string;
  endDate: string;
};

type FocusRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type EventPreviewConfig = {
  image?: string;
  itemImage?: Record<string, string>;
  itemLabel?: Record<string, string>;
  focus?: FocusRect;
  itemFocus?: Record<string, FocusRect>;
  note?: string;
  explanationOnly?: boolean;
  contextOnly?: boolean;
  unavailable?: boolean;
  unavailableItems?: string[];
};

type SelectedInteraction = {
  metricKey: string;
  itemKey?: string;
};

const rect = (
  x: number,
  y: number,
  width: number,
  height: number
): FocusRect => ({ x, y, width, height });

const getMetricKey = (eventName: string, property: string | null) =>
  `${eventName}::${property ?? ''}`;

const GOLF_MAP_HOLE_FOCUS: Record<string, FocusRect> = {
  mauna_1: rect(8, 43, 8, 7),
  mauna_2: rect(10, 41, 8, 7),
  mauna_3: rect(30, 42, 8, 7),
  mauna_4: rect(43, 44, 8, 7),
  mauna_5: rect(52, 42, 8, 7),
  mauna_6: rect(64, 45, 8, 7),
  mauna_7: rect(58, 44, 8, 7),
  mauna_8: rect(48, 45, 8, 7),
  mauna_9: rect(25, 45, 8, 7),
  ocean_1: rect(32, 46, 8, 7),
  ocean_2: rect(38, 47, 8, 7),
  ocean_3: rect(52, 47, 8, 7),
  ocean_4: rect(63, 49, 8, 7),
  ocean_5: rect(55, 50, 8, 7),
  ocean_6: rect(45, 49, 8, 7),
  ocean_7: rect(35, 51, 8, 7),
  ocean_8: rect(32, 49, 8, 7),
  ocean_9: rect(27, 48, 8, 7),
  blue_1: rect(71, 40, 8, 7),
  blue_2: rect(79, 41, 8, 7),
  blue_3: rect(83, 47, 8, 7),
  blue_4: rect(79, 61, 8, 7),
  blue_5: rect(74, 60, 8, 7),
  blue_6: rect(75, 57, 8, 7),
  blue_7: rect(78, 54, 8, 7),
  blue_8: rect(79, 48, 8, 7),
  blue_9: rect(68, 42, 8, 7),
};

const EVENT_PREVIEW_CONFIG: Partial<
  Record<PageRoute, Record<string, EventPreviewConfig>>
> = {
  '/': {
    'Click home_intent::type': {
      focus: rect(6, 22, 88, 47),
      itemFocus: {
        map: rect(6, 22, 88, 23),
        mission: rect(6, 22, 88, 23),
        info: rect(6, 22, 88, 23),
        golf: rect(6, 46, 88, 23),
      },
    },
  },
  '/map': {
    'Click top_btn::type': {
      focus: rect(2, 1, 96, 7),
      itemImage: {
        map_info: '/analytics/pages/states/map-full.png',
        map_exit: '/analytics/pages/states/map-full.png',
      },
      itemFocus: {
        gps: rect(2, 1, 22, 6),
        home: rect(25, 1, 62, 6),
        search: rect(3, 73, 30, 8),
        profile: rect(88, 1, 10, 6),
        map_info: rect(78, 1, 10, 6),
        map_exit: rect(88, 1, 10, 6),
      },
    },
    'Click navigation::type': {
      focus: rect(3, 66, 94, 15),
      itemFocus: {
        space_search: rect(3, 73, 30, 8),
        mission_reward: rect(34, 73, 32, 8),
        all_menu: rect(67, 73, 30, 8),
        review: rect(73, 66, 14, 6),
        bgm_on: rect(88, 66, 10, 6),
        bgm_off: rect(88, 66, 10, 6),
        home: rect(25, 1, 62, 6),
        profile: rect(88, 1, 10, 6),
        mission: rect(34, 73, 32, 8),
        coupon: rect(34, 73, 32, 8),
        back: rect(2, 1, 12, 7),
      },
      note: '지도 하단 주요 메뉴와 후기·배경음악 버튼을 같은 기준으로 비교합니다.',
    },
    'Click menu_float_btn::': {
      focus: rect(67, 73, 30, 8),
      note: '전체 메뉴를 여는 버튼 위치입니다.',
    },
    'Click menu_item::type': {
      image: '/analytics/pages/states/map-menu-open.png',
      focus: rect(3, 48, 94, 50),
      itemFocus: {
        facility: rect(3, 48, 46, 19),
        golf: rect(51, 48, 46, 19),
        mission: rect(3, 68, 46, 19),
        coupon: rect(51, 68, 46, 19),
        share: rect(72, 90, 22, 7),
      },
      note: '전체 메뉴 시트가 열린 상태에서 선택한 카드나 공유 버튼을 표시합니다.',
    },
    'Click minimap::': {
      focus: rect(3, 82, 94, 17),
    },
    'Click map_item::type': {
      image: '/analytics/pages/states/map-full.png',
      focus: rect(2, 8, 96, 84),
      itemFocus: {
        render_point: rect(26, 5, 8, 5),
        space: rect(34, 45, 14, 8),
        space_group: rect(7, 46, 26, 9),
      },
      note: '파란 이동 포인트, 일반 공간, 마우나빌·클럽하우스 공간그룹 중 실제 선택한 아이콘을 표시합니다.',
    },
    'Click hotspot_item::type': {
      image: '/analytics/pages/map.png',
      focus: rect(37, 32, 45, 23),
      itemImage: {
        horongi: '/analytics/pages/states/map-horongi-hotspot.png',
      },
      itemFocus: {
        move: rect(67, 44, 15, 10),
        space: rect(55, 39, 20, 12),
        space_group: rect(37, 32, 25, 13),
        horongi: rect(32, 37, 37, 24),
      },
      note: '산책 뷰어에서 선택한 이동 포인트, 일반 공간, 공간그룹 또는 미션 호롱이를 표시합니다.',
    },
    'Click mission_cta::type': {
      image: '/analytics/pages/states/map-horongi-progress.png',
      focus: rect(0, 2, 52, 18),
      itemImage: {
        horongi: '/analytics/pages/states/map-horongi-progress.png',
        stamp_tour: '/analytics/pages/states/map-stamp-tour-progress.png',
      },
      itemFocus: {
        horongi: rect(0, 2, 52, 18),
        stamp_tour: rect(2, 16, 51, 9),
      },
      note: '지도에서 호롱이 찾기와 뷰포인트 스탬프투어 진행 카드를 선택한 횟수를 비교합니다.',
    },
    'View space_group_bs::title': {
      image: '/analytics/pages/states/map-space-group-mauna.png',
      focus: rect(0, 52, 100, 48),
      note: '마우나빌콘도미니엄 공간 그룹을 선택해 안내 바텀시트가 열린 상태입니다.',
    },
    'Click space_item_in_group_bs::title': {
      image: '/analytics/pages/states/map-space-group-mauna.png',
      focus: rect(4, 59, 92, 11),
      note: '마우나빌콘도미니엄 바텀시트 안에서 시설을 선택하는 카드 영역입니다.',
    },
    'Click space_modal_btn::type': {
      image: '/analytics/pages/states/map-space-modal.png',
      focus: rect(5, 18, 90, 64),
      itemImage: {
        visit: '/analytics/pages/states/map-space-visit.png',
      },
      itemFocus: {
        detail: rect(8, 64, 25, 6),
        xr: rect(8, 72, 41, 8),
        directions: rect(50, 72, 42, 8),
        visit: rect(75, 67, 17, 7),
      },
      note: '실제 공간 상세 안내에서 실행한 CTA 위치를 표시합니다.',
    },
    'Click onboarding_btn::dont_show_today': {
      image: '/analytics/pages/states/map-onboarding.png',
      focus: rect(12, 47, 76, 10),
      itemFocus: {
        True: rect(36, 53, 28, 4),
        False: rect(12, 47, 76, 7),
      },
      note: '지도 진입 시 실제 온보딩이 표시된 상태에서 시작 행동을 구분합니다.',
    },
    'Click rate_modal_btn::type': {
      image: '/analytics/pages/states/rate-modal.png',
      focus: rect(10, 8, 80, 84),
      itemFocus: {
        close: rect(78, 8, 12, 8),
        never_show: rect(34, 87, 32, 5),
        submit: rect(15, 77, 70, 8),
      },
      note: '별점·리뷰 모달이 열린 상태의 실제 화면입니다.',
    },
  },
  '/info': {
    'Click facility_filter::type': {
      focus: rect(2, 16, 96, 8),
      itemFocus: {
        category: rect(2, 16, 58, 8),
        search_open: rect(86, 16, 12, 8),
        search_close: rect(86, 16, 12, 8),
      },
    },
    'Click facility_card_action::type': {
      focus: rect(3, 72, 94, 10),
      itemFocus: {
        detail: rect(4, 70, 25, 6),
        xr: rect(4, 76, 46, 7),
        directions: rect(51, 76, 46, 7),
      },
    },
    'Click event_float_btn::': {
      focus: rect(2, 8, 96, 7),
    },
  },
  '/mission': {
    'View mission_modal::title': {
      image: '/analytics/pages/states/mission-modal.png',
      focus: rect(5, 18, 90, 62),
      note: '미션 상세 모달이 열린 상태의 실제 화면입니다.',
    },
    'Click mission_item::type': {
      image: '/analytics/pages/states/mission-all-states.png',
      focus: rect(3, 27, 95, 69),
      itemFocus: {
        participate: rect(80, 44, 18, 9),
        receive: rect(84, 28, 14, 9),
      },
      note: '참여 가능과 선물 수령 가능 상태가 함께 있는 실제 미션 화면입니다.',
    },
    'Click mission_modal_tab::type': {
      image: '/analytics/pages/states/mission-modal.png',
      itemImage: {
        content: '/analytics/pages/states/mission-modal.png',
        howto: '/analytics/pages/states/mission-howto.png',
        approval_qr: '/analytics/pages/states/mission-approval-qr.png',
      },
      focus: rect(12, 25, 76, 7),
      itemFocus: {
        content: rect(12, 25, 28, 8),
        howto: rect(51, 10, 41, 8),
        approval_qr: rect(71, 29, 21, 6),
      },
      note: '미션 상세 안에서 정보를 전환하는 탭 영역입니다.',
    },
    'Click ranking_btn::': {
      focus: rect(81, 1, 16, 6),
    },
    'Click reward_flow::type': {
      image: '/analytics/pages/states/mission-reward-received.png',
      focus: rect(44, 55, 29, 7),
      itemFocus: {
        open_coupon: rect(44, 55, 29, 7),
      },
      note: '리워드 수령 성공 뒤 열린 안내에서 쿠폰함으로 이동하는 버튼입니다.',
    },
    'Complete mission_action::result': {
      explanationOnly: true,
      note: '참여하기 또는 리워드 받기 버튼을 누른 뒤 백엔드 API 응답이 끝나는 순간 기록됩니다. success는 처리가 정상 완료된 경우, failed는 서버 오류 등으로 처리가 완료되지 않은 경우입니다. 특정 화면 위치를 누르는 이벤트가 아니라 처리 결과를 집계하는 이벤트입니다.',
    },
    'Check mission_approval::result': {
      image: '/analytics/pages/states/mission-approval-qr.png',
      contextOnly: true,
      note: '승인 QR 화면에서 승인 상태 조회가 끝난 뒤 approved, pending, failed 결과를 기록합니다. 화면은 흐름 확인용이며 특정 위치를 강조하지 않습니다.',
    },
    'View mission_complete_modal::title': {
      image: '/analytics/pages/states/mission-complete-modal.png',
      focus: rect(13, 27, 74, 46),
      note: '미션 목표를 채운 순간 화면 중앙에 완료 안내가 표시됩니다.',
    },
    'Click mission_complete_modal::type': {
      image: '/analytics/pages/states/mission-complete-modal.png',
      focus: rect(13, 27, 74, 46),
      itemFocus: {
        close: rect(17, 63, 25, 8),
        view_mission: rect(42, 63, 41, 8),
      },
      note: '미션 완료 안내의 닫기·미션 보기 행동입니다.',
    },
  },
  '/mycoupon': {
    'Click reward_flow::type': {
      focus: rect(3, 26, 94, 25),
      itemImage: {
        show_qr: '/analytics/pages/states/coupon-qr.png',
      },
      itemFocus: {
        show_qr: rect(8, 22, 84, 52),
      },
      note: '보유 쿠폰 카드의 사용하기 버튼을 누르면 QR이 열립니다.',
    },
  },
  '/golf': {
    'Click golf_navigation::type': {
      focus: rect(3, 35, 94, 59),
      itemFocus: {
        course: rect(3, 35, 94, 7),
        map: rect(3, 42, 94, 7),
        hole: rect(3, 51, 94, 7),
        xr: rect(3, 80, 94, 7),
        previous: rect(3, 87, 46, 7),
        next: rect(51, 87, 46, 7),
        reserve: rect(28, 95, 44, 4),
        back: rect(2, 1, 11, 6),
      },
      note: '지도로 선택하기는 지도 화면이 아니라 이 버튼을 누른 횟수입니다.',
    },
    'Click golf_map_select::course_hole': {
      image: '/analytics/pages/states/golf-map.png',
      focus: rect(1, 40, 98, 28),
      itemFocus: GOLF_MAP_HOLE_FOCUS,
      note: '전체 코스 지도에서 실제로 선택한 코스와 홀 번호를 표시합니다.',
    },
    'Click golf_xr_btn::course': {
      focus: rect(3, 80, 94, 7),
    },
  },
  '/golf/xr': {
    'View xr_content::content_type': {
      image: '/analytics/pages/states/golf-viewer.png',
      focus: rect(1, 7, 98, 91),
      note: 'iframe 문서 로드가 아니라 첫 골프 XR 파노라마가 준비된 시점입니다. 이후 이동할 모든 콘텐츠의 선로딩 완료를 뜻하지는 않습니다.',
    },
    'Click xr_move_btn::': {
      image: '/analytics/pages/states/golf-viewer.png',
      focus: rect(35, 20, 30, 20),
      note: '골프 XR 화면 안에서 바닥의 이동 방향 버튼을 누른 횟수입니다.',
    },
    'Click xr_navigation::type': {
      image: '/analytics/pages/states/golf-viewer.png',
      focus: rect(2, 1, 11, 6),
    },
    'Click onboarding_btn::dont_show_today': {
      image: '/analytics/pages/states/golf-onboarding.jpg',
      focus: rect(12, 48, 76, 12),
      itemFocus: {
        True: rect(37, 57, 27, 4),
        False: rect(12, 48, 76, 8),
      },
      note: 'True는 오늘 다시 보지 않기 체크 영역, False는 체크하지 않고 바로 시작한 골프장 보러가기 버튼입니다.',
    },
  },
  '/space/xr': {
    'View xr_content::content_type': {
      focus: rect(1, 1, 98, 98),
      note: 'iframe 문서 로드가 아니라 첫 공간 XR 파노라마가 준비된 시점입니다. 이후 이동할 모든 콘텐츠의 선로딩 완료를 뜻하지는 않습니다.',
    },
    'Click xr_move_btn::': {
      image: '/analytics/pages/states/map-xr-hotspots.png',
      focus: rect(8, 58, 25, 13),
      note: '공간 XR 화면 안에서 바닥의 이동 지점 버튼을 누른 횟수입니다.',
    },
    'Click xr_navigation::type': {
      focus: rect(2, 2, 10, 7),
    },
  },
  '/login': {
    'Click auth_btn::type': {
      focus: rect(73, 50, 25, 7),
      itemImage: {
        resend_code: '/analytics/pages/states/login-verification.png',
        continue: '/analytics/pages/states/login-verification.png',
        privacy_terms: '/analytics/pages/states/login-signup.png',
        service_terms: '/analytics/pages/states/login-signup.png',
        signup: '/analytics/pages/states/login-signup.png',
      },
      itemLabel: {
        privacy_terms: '개인정보 약관 보기',
        service_terms: '서비스 이용약관 보기',
      },
      itemFocus: {
        request_code: rect(73, 50, 25, 7),
        resend_code: rect(79, 44, 19, 6),
        continue: rect(2, 63, 96, 6),
        privacy_terms: rect(92, 33, 6, 4),
        service_terms: rect(92, 37, 6, 4),
        signup: rect(2, 43, 96, 7),
      },
      note: '인증번호 요청·재요청·계속하기와 신규 회원의 닉네임·필수 약관 동의 화면을 행동에 맞게 구분합니다.',
    },
    'Complete auth::type': {
      itemImage: {
        login: '/analytics/pages/login.png',
        signup_required: '/analytics/pages/states/login-signup.png',
        signup: '/analytics/pages/states/login-signup.png',
      },
      contextOnly: true,
      note: '인증 처리 결과에 따라 기존 사용자 로그인 또는 신규 가입 화면으로 분기됩니다. 특정 버튼 위치가 아니라 처리 완료 시점에 기록됩니다.',
    },
    'Complete auth::result': {
      explanationOnly: true,
      note: '휴대폰 인증·로그인·가입 처리의 성공 또는 실패 결과를 집계합니다. 특정 화면 위치와 직접 대응하지 않는 결과 이벤트입니다.',
    },
  },
  '/user': {
    'Click user_profile_action::type': {
      image: '/analytics/pages/states/user-profile.png',
      focus: rect(2, 28, 96, 70),
      itemFocus: {
        ranking: rect(29, 30, 24, 5),
        medal: rect(2, 52, 96, 46),
      },
      note: '현재 프로필 화면에서 명예의 전당 이동 버튼과 월별 호롱이 메달 영역을 표시합니다.',
    },
    'Click signout_btn::': {
      image: '/analytics/pages/states/user-signout.png',
      focus: rect(2, 92, 96, 5),
      note: '프로필 화면 하단의 로그아웃 버튼 위치입니다.',
    },
    'View medal_modal::source': {
      unavailable: true,
      note: '메달 상세 모달이 열린 정확한 화면 자료가 없어 임의 화면을 표시하지 않습니다.',
    },
    'Click medal_modal::type': {
      unavailable: true,
      note: '메달 상세 모달의 버튼이 보이는 정확한 화면 자료가 없어 임의 위치를 표시하지 않습니다.',
    },
  },
  '/mission/rank': {
    'Click ranking_guide::type': {
      focus: rect(64, 8, 33, 7),
      itemImage: {
        start_walking: '/analytics/pages/states/ranking-walking-guide.png',
      },
      itemFocus: {
        open_top: rect(64, 8, 33, 7),
        open_help: rect(86, 18, 10, 7),
        retry: rect(3, 22, 94, 60),
        start_walking: rect(9, 81, 82, 6),
      },
      note: '걷기 시작은 안내 모달 하단의 지도에서 걸어보기 버튼을 누른 행동입니다.',
    },
  },
};

const PAGE_OPTIONS: Array<{
  route: PageRoute;
  label: string;
  preview: string;
  previewAlt: string;
}> = [
  {
    route: '/',
    label: '메인',
    preview: '/analytics/pages/home.png',
    previewAlt: '오늘 뭘 해볼까요 메인 화면',
  },
  {
    route: '/map',
    label: '리조트 지도',
    preview: '/analytics/pages/map.png',
    previewAlt: '마우나 스마트가이드 리조트 지도 화면',
  },
  {
    route: '/info',
    label: '시설 안내',
    preview: '/analytics/pages/info.png',
    previewAlt: '시설 안내 화면',
  },
  {
    route: '/mission',
    label: '미션',
    preview: '/analytics/pages/mission.png',
    previewAlt: '미션 참여 화면',
  },
  {
    route: '/mycoupon',
    label: '쿠폰',
    preview: '/analytics/pages/mycoupon.png',
    previewAlt: '나의 쿠폰 화면',
  },
  {
    route: '/golf',
    label: '골프',
    preview: '/analytics/pages/golf.png',
    previewAlt: '골프 코스 공략 화면',
  },
  {
    route: '/golf/xr',
    label: '골프 XR',
    preview: '/analytics/pages/golf-xr.png',
    previewAlt: '골프 XR 공략 화면',
  },
  {
    route: '/space/xr',
    label: '공간 XR',
    preview: '/analytics/pages/states/map-xr-hotspots.png',
    previewAlt: '리조트 공간 XR 화면',
  },
  {
    route: '/login',
    label: '휴대폰 인증',
    preview: '/analytics/pages/login.png',
    previewAlt: '휴대폰 인증 화면',
  },
  {
    route: '/user',
    label: '프로필',
    preview: '/analytics/pages/states/user-profile.png',
    previewAlt: '사용자 프로필 화면',
  },
  {
    route: '/mission/rank',
    label: '명예의 전당',
    preview: '/analytics/pages/ranking.png',
    previewAlt: '미션 명예의 전당 화면',
  },
];

const formatNumber = (value: number) => value.toLocaleString('ko-KR');
const isLegacyLabel = (value: string) => value.includes('개편 전');

const MaunaPageAnalytics = ({ startDate, endDate }: Props) => {
  const [selectedRoute, setSelectedRoute] = useState<PageRoute>('/');
  const [selectedInteraction, setSelectedInteraction] =
    useState<SelectedInteraction | null>(null);
  const [previewAspectRatio, setPreviewAspectRatio] = useState(9 / 16);
  const selectedPage = PAGE_OPTIONS.find(
    (page) => page.route === selectedRoute
  )!;

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['getPageAnalyticsMauna', selectedRoute, startDate, endDate],
    queryFn: () =>
      maunaSpaceAPI.getPageAnalytics(
        { code: import.meta.env.VITE_SECRET_API_KEY },
        { page: selectedRoute, startDate, endDate }
      ),
    enabled: Boolean(startDate && endDate),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const analytics = data?.data as PageAnalyticsResponse | undefined;
  const visibleMetrics = useMemo(
    () =>
      (analytics?.metrics ?? [])
        .filter((metric) => !isLegacyLabel(metric.description))
        .map((metric) => {
          if (metric.status === 'error') return metric;

          const items = metric.items.filter(
            (item) => !isLegacyLabel(item.label)
          );

          return {
            ...metric,
            items,
            total: items.reduce((sum, item) => sum + item.total, 0),
          };
        }),
    [analytics]
  );
  const visibleInteractionTotal = useMemo(
    () =>
      visibleMetrics.reduce(
        (sum, metric) =>
          metric.status === 'success' ? sum + metric.total : sum,
        0
      ),
    [visibleMetrics]
  );
  const maxInteractionCount = useMemo(
    () =>
      Math.max(
        1,
        ...visibleMetrics.flatMap((metric) =>
          metric.items.map((item) => item.total)
        )
      ),
    [visibleMetrics]
  );
  const selectedMetric = visibleMetrics.find(
    (metric) =>
      getMetricKey(metric.eventName, metric.property) ===
      selectedInteraction?.metricKey
  );
  const selectedMetricItem = selectedMetric?.items.find(
    (item) => item.key === selectedInteraction?.itemKey
  );
  const selectedPreviewConfig = selectedInteraction
    ? EVENT_PREVIEW_CONFIG[selectedRoute]?.[selectedInteraction.metricKey]
    : undefined;
  const selectedPreviewImage =
    (selectedInteraction?.itemKey
      ? selectedPreviewConfig?.itemImage?.[selectedInteraction.itemKey]
      : undefined) ??
    selectedPreviewConfig?.image ??
    selectedPage.preview;
  const selectedItemHasNoMapping = Boolean(
    selectedInteraction?.itemKey &&
      selectedPreviewConfig?.itemFocus &&
      !selectedPreviewConfig.itemFocus[selectedInteraction.itemKey]
  );
  const isPreviewUnavailable = Boolean(
    selectedInteraction &&
      (!selectedPreviewConfig ||
        selectedPreviewConfig.unavailable ||
        (selectedInteraction.itemKey &&
          selectedPreviewConfig.unavailableItems?.includes(
            selectedInteraction.itemKey
          )) ||
        selectedItemHasNoMapping)
  );
  const selectedFocus = selectedPreviewConfig
    ? selectedPreviewConfig.contextOnly
      ? undefined
      : (selectedInteraction?.itemKey
        ? selectedPreviewConfig.itemFocus?.[selectedInteraction.itemKey]
        : undefined) ?? selectedPreviewConfig.focus
    : undefined;
  const selectedInteractionLabel = selectedMetricItem
    ? (selectedPreviewConfig?.itemLabel?.[selectedMetricItem.key] ??
      selectedMetricItem.label)
    : selectedMetric?.description;

  useEffect(() => {
    if (!selectedInteraction || !analytics) return;

    const metric = visibleMetrics.find(
      (item) =>
        getMetricKey(item.eventName, item.property) ===
        selectedInteraction.metricKey
    );
    const hasSelectedItem =
      !selectedInteraction.itemKey ||
      metric?.items.some((item) => item.key === selectedInteraction.itemKey);

    if (!metric || !hasSelectedItem) setSelectedInteraction(null);
  }, [analytics, selectedInteraction, visibleMetrics]);

  return (
    <PageAnalyticsRoot>
      <PageTabScroller role="tablist" aria-label="분석할 서비스 화면">
        {PAGE_OPTIONS.map((page) => (
          <PageTab
            key={page.route}
            type="button"
            role="tab"
            aria-selected={selectedRoute === page.route}
            $active={selectedRoute === page.route}
            onClick={() => {
              setSelectedRoute(page.route);
              setSelectedInteraction(null);
            }}
          >
            <span>{page.label}</span>
            <small>{page.route}</small>
          </PageTab>
        ))}
      </PageTabScroller>

      {isLoading || isFetching ? (
        <StatusStage aria-live="polite">
          <LoadingRing aria-hidden="true" />
          <strong>{selectedPage.label} 행동 데이터를 집계하고 있습니다.</strong>
          <span>선택 기간의 Amplitude 이벤트를 불러오는 중입니다.</span>
        </StatusStage>
      ) : isError || !analytics ? (
        <StatusStage role="alert">
          <AlertCircle size={28} aria-hidden="true" />
          <strong>페이지 분석 데이터를 불러오지 못했습니다.</strong>
          <span>잠시 후 다시 시도해 주세요.</span>
          <RetryButton type="button" onClick={() => void refetch()}>
            <RefreshCw size={16} aria-hidden="true" />
            다시 불러오기
          </RetryButton>
        </StatusStage>
      ) : (
        <AnalysisLayout>
          <PreviewColumn>
            <PreviewHeading>
              <div>
                <RouteBadge>{analytics.page.route}</RouteBadge>
                <h2>{analytics.page.title}</h2>
              </div>
              <PreviewPeriod>
                {analytics.period.startDate} ~ {analytics.period.endDate}
              </PreviewPeriod>
            </PreviewHeading>
            {selectedInteractionLabel && (
              <PreviewSelection aria-live="polite">
                <ScanSearch size={16} aria-hidden="true" />
                <span>
                  선택한 기능 <strong>{selectedInteractionLabel}</strong>
                </span>
              </PreviewSelection>
            )}
            {isPreviewUnavailable ? (
              <UnavailablePreview role="status">
                <AlertCircle size={28} aria-hidden="true" />
                <div>
                  <strong>올바른 화면이 없습니다.</strong>
                  <p>
                    {selectedPreviewConfig?.note ??
                      '선택한 이벤트와 정확히 대응하는 화면 자료가 없어 임의 화면을 표시하지 않습니다.'}
                  </p>
                </div>
              </UnavailablePreview>
            ) : selectedPreviewConfig?.explanationOnly ? (
              <ExplanationPanel>
                <Activity size={28} aria-hidden="true" />
                <div>
                  <strong>화면 위치가 없는 처리 결과 이벤트</strong>
                  <p>{selectedPreviewConfig.note}</p>
                </div>
              </ExplanationPanel>
            ) : (
              <>
                <DeviceFrame $aspectRatio={previewAspectRatio}>
                  <PreviewImage
                    src={selectedPreviewImage}
                    alt={
                      selectedInteractionLabel
                        ? `${selectedPage.previewAlt} - ${selectedInteractionLabel}`
                        : selectedPage.previewAlt
                    }
                    onLoad={({ currentTarget }) =>
                      setPreviewAspectRatio(
                        currentTarget.naturalWidth / currentTarget.naturalHeight
                      )
                    }
                  />
                  {selectedFocus && (
                    <HighlightBox
                      aria-hidden="true"
                      style={{
                        left: `${selectedFocus.x}%`,
                        top: `${selectedFocus.y}%`,
                        width: `${selectedFocus.width}%`,
                        height: `${selectedFocus.height}%`,
                      }}
                    />
                  )}
                </DeviceFrame>
                <PreviewCaption>
                  {selectedPreviewConfig?.note ??
                    (selectedInteractionLabel
                      ? '붉은 영역이 선택한 이벤트가 발생하는 화면 위치입니다.'
                      : '좌측 화면은 기능 위치를 확인하기 위한 기준 화면입니다. 우측 수치는 선택 기간의 실제 이용 데이터입니다.')}
                </PreviewCaption>
              </>
            )}
          </PreviewColumn>

          <AnalyticsColumn>
            <AnalyticsHeading>
              <div>
                <Eyebrow>PAGE INTERACTION</Eyebrow>
                <h2>{analytics.page.title} 행동 분석</h2>
                <p>{analytics.page.description}</p>
              </div>
            </AnalyticsHeading>

            <SummaryGrid>
              <SummaryCard>
                <SummaryIcon $tone="blue">
                  <Eye size={20} aria-hidden="true" />
                </SummaryIcon>
                <span>페이지 조회</span>
                <strong>{formatNumber(analytics.views.total)}</strong>
                <small>View Page · totals</small>
              </SummaryCard>
              <SummaryCard>
                <SummaryIcon $tone="green">
                  <Users size={20} aria-hidden="true" />
                </SummaryIcon>
                <span>순사용자</span>
                <strong>{formatNumber(analytics.views.unique)}</strong>
                <small>View Page · uniques</small>
              </SummaryCard>
              <SummaryCard>
                <SummaryIcon $tone="amber">
                  <MousePointerClick size={20} aria-hidden="true" />
                </SummaryIcon>
                <span>측정된 상호작용</span>
                <strong>{formatNumber(visibleInteractionTotal)}</strong>
                <small>아래 이벤트 합계</small>
              </SummaryCard>
            </SummaryGrid>

            <InteractionSection>
              <SectionHeading>
                <div>
                  <Activity size={20} aria-hidden="true" />
                  <h3>버튼·기능별 이용 현황</h3>
                </div>
                <span>클릭 수가 많은 순서로 표시</span>
              </SectionHeading>

              {visibleMetrics.length === 0 ? (
                <MetricMessage>
                  선택 기간에 수집된 버튼·기능 이용 기록이 없습니다.
                </MetricMessage>
              ) : visibleMetrics.map((metric) => {
                const metricKey = getMetricKey(
                  metric.eventName,
                  metric.property
                );
                const isMetricSelected =
                  selectedInteraction?.metricKey === metricKey &&
                  !selectedInteraction.itemKey;
                const metricPreviewConfig =
                  EVENT_PREVIEW_CONFIG[selectedRoute]?.[metricKey];
                return (
                <EventGroup
                  key={`${metric.eventName}-${metric.property ?? ''}`}
                  $active={selectedInteraction?.metricKey === metricKey}
                >
                  <EventHeader
                    type="button"
                    aria-pressed={isMetricSelected}
                    onClick={() =>
                      setSelectedInteraction({ metricKey })
                    }
                  >
                    <div>
                      <h4>{metric.description}</h4>
                      <EventDefinition>
                        <code>{metric.eventName}</code>
                        <span>Property</span>
                        <code>{metric.property ?? '없음'}</code>
                      </EventDefinition>
                    </div>
                    <EventHeaderSide>
                      <EventTotal>{formatNumber(metric.total)}회</EventTotal>
                      <EventLocationHint>
                        <ScanSearch size={14} aria-hidden="true" />
                        {metricPreviewConfig?.explanationOnly
                          ? '집계 설명'
                          : metricPreviewConfig?.unavailable || !metricPreviewConfig
                            ? '예시 없음'
                          : metricPreviewConfig?.contextOnly
                            ? '참고 화면'
                          : '화면 위치'}
                      </EventLocationHint>
                    </EventHeaderSide>
                  </EventHeader>

                  {metric.status === 'error' ? (
                    <MetricMessage $error>
                      이 이벤트는 일시적으로 집계하지 못했습니다.
                    </MetricMessage>
                  ) : metric.items.length === 0 ? (
                    <MetricMessage>선택 기간에 수집된 클릭이 없습니다.</MetricMessage>
                  ) : (
                    <MetricRows>
                      {metric.items.map((item) => {
                        const share = metric.total
                          ? (item.total / metric.total) * 100
                          : 0;
                        const isItemSelected =
                          selectedInteraction?.metricKey === metricKey &&
                          selectedInteraction.itemKey === item.key;
                        return (
                          <MetricRow
                            key={`${metric.eventName}-${item.key}`}
                            type="button"
                            aria-pressed={isItemSelected}
                            $active={isItemSelected}
                            onClick={() =>
                              setSelectedInteraction({
                                metricKey,
                                itemKey: item.key,
                              })
                            }
                          >
                            <MetricLabel>
                              <MetricName>
                                <strong>
                                  {metricPreviewConfig?.itemLabel?.[item.key] ??
                                    item.label}
                                </strong>
                                {metric.property && item.key !== item.label && (
                                  <PropertyValue>{item.key}</PropertyValue>
                                )}
                              </MetricName>
                              {metric.property && (
                                <span>{share.toFixed(1)}%</span>
                              )}
                            </MetricLabel>
                            <BarTrack aria-hidden="true">
                              <BarFill
                                style={{
                                  width: `${Math.max(
                                    item.total > 0 ? 3 : 0,
                                    (item.total / maxInteractionCount) * 100
                                  )}%`,
                                }}
                              />
                            </BarTrack>
                            <MetricValue>{formatNumber(item.total)}회</MetricValue>
                          </MetricRow>
                        );
                      })}
                    </MetricRows>
                  )}
                </EventGroup>
                );
              })}
            </InteractionSection>

            <MeasurementNote>
              <strong>측정 기준</strong>
              <span>
                버튼을 누르는 순간 위 Event가 1회 기록되며, Property 값으로 어떤
                기능을 선택했는지 구분합니다. 비율은 Property가 있는 같은 Event
                안에서의 선택 비중이며, Property가 없는 단일 이벤트는 횟수만
                표시합니다. 동일 사용자의 반복 클릭도 클릭 수에 포함됩니다.
              </span>
            </MeasurementNote>
          </AnalyticsColumn>
        </AnalysisLayout>
      )}
    </PageAnalyticsRoot>
  );
};

export default MaunaPageAnalytics;

const PageAnalyticsRoot = styled.section`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const PageTabScroller = styled.div`
  display: flex;
  gap: 8px;
  padding: 4px 2px 10px;
  overflow-x: auto;
  scrollbar-width: thin;
`;

const PageTab = styled.button<{ $active: boolean }>`
  min-width: 112px;
  height: 54px;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 2px;
  padding: 0 14px;
  border: 1px solid ${({ $active }) => ($active ? '#1f6feb' : '#d8dee8')};
  border-radius: 7px;
  background: ${({ $active }) => ($active ? '#eef4ff' : '#ffffff')};
  color: ${({ $active }) => ($active ? '#1549a3' : '#323846')};
  cursor: pointer;

  span {
    font-size: 0.85rem;
    font-weight: 750;
  }

  small {
    color: ${({ $active }) => ($active ? '#3c6ec5' : '#8992a3')};
    font-size: 0.68rem;
  }
`;

const AnalysisLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 0.8fr) minmax(520px, 1.7fr);
  align-items: start;
  gap: 28px;

  @media (max-width: 1040px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewColumn = styled.div`
  position: sticky;
  top: 18px;
  min-width: 0;

  @media (max-width: 1040px) {
    position: static;
  }
`;

const PreviewHeading = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;

  h2 {
    margin: 5px 0 0;
    color: #1f2430;
    font-size: 1.05rem;
  }
`;

const RouteBadge = styled.code`
  color: #1762e8;
  font-size: 0.76rem;
  font-weight: 700;
`;

const PreviewPeriod = styled.span`
  flex: 0 0 auto;
  color: #7c8595;
  font-size: 0.72rem;
`;

const PreviewSelection = styled.div`
  min-height: 38px;
  display: flex;
  align-items: center;
  gap: 8px;
  width: min(100%, 390px);
  margin: 0 auto 10px;
  padding: 0 11px;
  border: 1px solid #f0b6ba;
  border-radius: 6px;
  background: #fff5f5;
  color: #a62c35;
  font-size: 0.73rem;

  strong {
    margin-left: 3px;
    color: #7f1d27;
  }
`;

const DeviceFrame = styled.div<{ $aspectRatio: number }>`
  position: relative;
  width: auto;
  max-width: 100%;
  height: min(
    ${({ $aspectRatio }) => 390 / $aspectRatio}px,
    calc(100vh - 290px)
  );
  min-height: 360px;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  margin: 0 auto;
  overflow: hidden;
  border: 1px solid #cfd6e1;
  border-radius: 12px;
  background: #f4f6f9;
  box-shadow: 0 12px 30px rgba(24, 33, 48, 0.14);
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  background: #f4f6f9;
`;

const HighlightBox = styled.span`
  position: absolute;
  z-index: 2;
  border: 3px solid #ef3340;
  border-radius: 5px;
  box-shadow:
    0 0 0 999px rgba(16, 24, 40, 0.18),
    0 0 0 2px rgba(255, 255, 255, 0.8),
    0 4px 14px rgba(239, 51, 64, 0.35);
  pointer-events: none;
  animation: preview-focus-pulse 1.6s ease-in-out infinite;

  @keyframes preview-focus-pulse {
    0%,
    100% {
      border-color: #ef3340;
    }
    50% {
      border-color: #ff6873;
    }
  }
`;

const PreviewCaption = styled.p`
  max-width: 390px;
  margin: 12px auto 0;
  color: #70798a;
  font-size: 0.75rem;
  line-height: 1.5;
`;

const ExplanationPanel = styled.div`
  display: flex;
  gap: 16px;
  width: min(100%, 390px);
  margin: 0 auto;
  padding: 24px;
  border: 1px solid #cfd9e8;
  border-radius: 8px;
  background: #f5f8fc;
  color: #1c57b7;

  div {
    min-width: 0;
  }

  strong {
    display: block;
    color: #202938;
    font-size: 0.95rem;
  }

  p {
    margin: 8px 0 0;
    color: #5f6b7c;
    font-size: 0.8rem;
    line-height: 1.65;
    word-break: keep-all;
  }
`;

const UnavailablePreview = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: min(100%, 390px);
  min-height: 220px;
  margin: 0 auto;
  padding: 24px;
  border: 1px solid #d8dee8;
  border-radius: 8px;
  background: #f7f8fa;
  color: #7c8595;

  div {
    min-width: 0;
  }

  strong {
    display: block;
    color: #303746;
    font-size: 0.95rem;
  }

  p {
    margin: 8px 0 0;
    color: #6f7888;
    font-size: 0.8rem;
    line-height: 1.65;
  }
`;

const AnalyticsColumn = styled.div`
  min-width: 0;
`;

const AnalyticsHeading = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;

  h2 {
    margin: 4px 0 6px;
    color: #171b23;
    font-size: 1.35rem;
  }

  p {
    margin: 0;
    color: #687184;
    font-size: 0.84rem;
    line-height: 1.5;
  }
`;

const Eyebrow = styled.span`
  color: #1762e8;
  font-size: 0.7rem;
  font-weight: 800;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 22px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  min-height: 132px;
  padding: 14px;
  border: 1px solid #dfe4ec;
  border-radius: 7px;
  background: #ffffff;

  > span {
    display: block;
    margin-top: 11px;
    color: #5f6879;
    font-size: 0.78rem;
  }

  > strong {
    display: block;
    margin-top: 4px;
    color: #181c24;
    font-size: 1.55rem;
  }

  > small {
    display: block;
    margin-top: 4px;
    color: #929aaa;
    font-size: 0.66rem;
  }
`;

const SummaryIcon = styled.div<{ $tone: 'blue' | 'green' | 'amber' }>`
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 7px;
  color: ${({ $tone }) =>
    $tone === 'blue' ? '#1762e8' : $tone === 'green' ? '#14805e' : '#b35b08'};
  background: ${({ $tone }) =>
    $tone === 'blue' ? '#edf3ff' : $tone === 'green' ? '#eaf8f2' : '#fff3e5'};
`;

const InteractionSection = styled.div`
  border-top: 1px solid #dfe4ec;
`;

const SectionHeading = styled.div`
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  > div {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #1f4f9f;
  }

  h3 {
    margin: 0;
    color: #20252f;
    font-size: 1rem;
  }

  > span {
    color: #8a93a2;
    font-size: 0.7rem;
  }
`;

const EventGroup = styled.article<{ $active: boolean }>`
  padding: 18px 0;
  border-top: 1px solid #edf0f4;
  background: ${({ $active }) => ($active ? '#f8faff' : 'transparent')};
  box-shadow: ${({ $active }) =>
    $active ? 'inset 3px 0 0 #2472e8' : 'none'};
`;

const EventHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
  padding: 0 8px;
  border: 0;
  background: transparent;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover h4,
  &:focus-visible h4 {
    color: #1762e8;
  }

  &:focus-visible {
    outline: 2px solid #8ab4f8;
    outline-offset: 4px;
  }

  h4 {
    margin: 0 0 7px;
    color: #242a35;
    font-size: 0.88rem;
  }
`;

const EventHeaderSide = styled.span`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`;

const EventLocationHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #7a8596;
  font-size: 0.66rem;
  white-space: nowrap;
`;

const EventDefinition = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;

  span {
    color: #9aa2af;
    font-size: 0.65rem;
  }

  code {
    padding: 3px 6px;
    border-radius: 4px;
    background: #f0f3f7;
    color: #4b5668;
    font-size: 0.66rem;
  }
`;

const EventTotal = styled.strong`
  flex: 0 0 auto;
  color: #1762e8;
  font-size: 0.92rem;
`;

const MetricRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

const MetricRow = styled.button<{ $active: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(138px, 0.8fr) minmax(160px, 1.3fr) 64px;
  align-items: center;
  gap: 12px;
  min-height: 34px;
  padding: 5px 8px;
  border: 1px solid ${({ $active }) => ($active ? '#8ab4f8' : 'transparent')};
  border-radius: 5px;
  background: ${({ $active }) => ($active ? '#eef4ff' : 'transparent')};
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f4f7fc;
  }

  &:focus-visible {
    outline: 2px solid #8ab4f8;
    outline-offset: 2px;
  }

  @media (max-width: 640px) {
    grid-template-columns: minmax(120px, 1fr) 58px;

    > div:nth-child(2) {
      grid-column: 1 / -1;
      grid-row: 2;
    }
  }
`;

const MetricLabel = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  > span {
    color: #9aa2af;
    font-size: 0.66rem;
  }
`;

const MetricName = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;

  strong {
    overflow: hidden;
    color: #3d4553;
    font-size: 0.78rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const PropertyValue = styled.code`
  flex: 0 0 auto;
  padding: 2px 5px;
  border-radius: 4px;
  background: #edf1f6;
  color: #6d7787;
  font-size: 0.62rem;
  line-height: 1.2;
`;

const BarTrack = styled.div`
  height: 9px;
  overflow: hidden;
  border-radius: 5px;
  background: #edf0f4;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: inherit;
  background: #2472e8;
`;

const MetricValue = styled.strong`
  color: #2e3541;
  font-size: 0.76rem;
  text-align: right;
`;

const MetricMessage = styled.div<{ $error?: boolean }>`
  padding: 11px 12px;
  border: 1px solid ${({ $error }) => ($error ? '#f1c4c4' : '#e4e8ee')};
  border-radius: 6px;
  background: ${({ $error }) => ($error ? '#fff3f3' : '#f7f8fa')};
  color: ${({ $error }) => ($error ? '#b33a3a' : '#818a99')};
  font-size: 0.74rem;
`;

const MeasurementNote = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  margin-top: 12px;
  padding: 14px;
  border-left: 3px solid #1762e8;
  background: #f3f7ff;

  strong {
    color: #174b9f;
    font-size: 0.76rem;
  }

  span {
    color: #647087;
    font-size: 0.74rem;
    line-height: 1.55;
  }
`;

const StatusStage = styled.div`
  min-height: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #6c7586;
  text-align: center;

  strong {
    color: #303744;
    font-size: 0.92rem;
  }

  span {
    font-size: 0.76rem;
  }
`;

const LoadingRing = styled.span`
  width: 28px;
  height: 28px;
  margin-bottom: 4px;
  border: 3px solid #d9e4f7;
  border-top-color: #1762e8;
  border-radius: 50%;
  animation: page-analytics-spin 0.8s linear infinite;

  @keyframes page-analytics-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const RetryButton = styled.button`
  height: 36px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 0 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  color: #3e4859;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
`;
